import { Button, Checkbox, Heading, Icon, IconButton, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, SimpleGrid, Stack, StackDivider, Table, TableContainer, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaTrash, FaUndo } from "react-icons/fa";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { GET, POST, DELETE } from "../../../utils/ApiRequestProvider";

interface RecycleBinProps {
    businessId?: number | string;
}

export default function RecycleBin({ businessId }: RecycleBinProps) {
    const toast = useToast();
    const [items, setItems] = useState<Array<any>>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [focused, setFocused] = useState<any>(null);
    const [deleteReason, setDeleteReason] = useState('');
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();
    const [loading, setLoading] = useState(false);

    const toggleOne = (id: string) => {
        setSelected((p) => {
            const n = new Set(p);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    };
    const allChecked = items.length > 0 && items.every((it) => selected.has(String(it.id)));
    const someChecked = items.some((it) => selected.has(String(it.id)));
    const toggleAll = () => {
        if (allChecked) setSelected(new Set());
        else setSelected(new Set(items.map((it) => String(it.id))));
    };

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const url = businessId
                ? `/api/Documents/${businessId}/DeletedDocs?sortColumn=Date&sortOrder=desc`
                : `/api/internal/deleted-documents?sortColumn=Date&sortOrder=desc`;
            const res = await GET(url);
            setItems(res.data?.documents ?? []);
        } catch {
            toast({ title: 'Could not load deleted documents', status: 'error', duration: 1500, position: 'top-right' });
        }
        setLoading(false);
    }, [businessId, toast]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const doRestore = async (ids: string[]) => {
        if (ids.length === 0) {
            toast({ title: 'No document selected', status: 'warning', duration: 1200, position: 'top-right' });
            return;
        }
        if (ids.length > 1 && !window.confirm(`Restore ${ids.length} documents?`)) return;
        try {
            const res = await POST('/api/Documents/restore', { documentIds: ids });
            if (res.status === 'success') {
                toast({ title: 'Restored', status: 'success', duration: 1200, position: 'top-right' });
                setSelected(new Set());
                fetchList();
            } else {
                toast({ title: res.message || 'Restore failed', status: 'error', duration: 1500, position: 'top-right' });
            }
        } catch {
            toast({ title: 'Restore failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    const confirmDelete = async () => {
        const ids = Array.from(selected);
        try {
            await DELETE(`/api/Documents/${ids.join(',')}/purge`, { reason: deleteReason });
            toast({ title: 'Deleted', status: 'success', duration: 1200, position: 'top-right' });
            setSelected(new Set());
            setDeleteReason('');
            onDelClose();
            fetchList();
        } catch {
            toast({ title: 'Delete failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'column', "2xl": "row" }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 6, "2xl": 0 }}
            >
                <Stack align={'flex-start'} justify={'start'} spacing={4}>
                    <Stack direction={'row'}>
                        <Icon as={RiDeleteBin5Fill} fontSize={'5xl'} color={'primary.400'} />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Recycle Bin</Heading>
                            <Text fontSize={'sm'}>All Recycle Bin Documents are automatically deleted after 90 Days</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack direction={'row'} spacing={2} align={'center'}>
                    {selected.size > 0 && (
                        <>
                            <Text fontSize={'sm'} color={'primary.600'}>{selected.size} selected</Text>
                            <Button
                                size={'sm'}
                                leftIcon={<FaUndo />}
                                colorScheme={'green'}
                                onClick={() => doRestore(Array.from(selected))}
                            >
                                Restore Selected
                            </Button>
                            <Button
                                size={'sm'}
                                leftIcon={<FaTrash />}
                                colorScheme={'red'}
                                onClick={onDelOpen}
                            >
                                Delete Permanently
                            </Button>
                        </>
                    )}
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <SimpleGrid
                    templateColumns={{ base: '1fr', md: '1fr 4fr 2fr' }}
                    spacing={6}
                >
                    <Stack w={'full'}>
                        <TableContainer>
                            <Table size='sm'>
                                <Thead h={'50px'} bg={'primary.400'}>
                                    <Tr>
                                        <Th color={'white'} width={'40px'}>
                                            <Checkbox
                                                isChecked={allChecked}
                                                isIndeterminate={!allChecked && someChecked}
                                                onChange={toggleAll}
                                                colorScheme={'whiteAlpha'}
                                            />
                                        </Th>
                                        <Th color={'white'} isNumeric>Document ID</Th>
                                        <Th color={'white'}>Document Type</Th>
                                        <Th color={'white'} width={'80px'}>Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {items.length === 0 ? (
                                        <Tr>
                                            <Td colSpan={4} textAlign={'center'}>
                                                {loading ? 'Loading…' : 'No deleted documents'}
                                            </Td>
                                        </Tr>
                                    ) : items.map((item) => (
                                        <Tr key={item.id} onClick={() => setFocused(item)} cursor={'pointer'} bg={focused?.id === item.id ? 'blue.50' : undefined}>
                                            <Td onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    isChecked={selected.has(String(item.id))}
                                                    onChange={() => toggleOne(String(item.id))}
                                                />
                                            </Td>
                                            <Td isNumeric>{item.id}</Td>
                                            <Td>{item.documentType ?? item.name ?? '—'}</Td>
                                            <Td onClick={(e) => e.stopPropagation()}>
                                                <IconButton
                                                    aria-label={'Restore'}
                                                    icon={<FaUndo />}
                                                    size={'xs'}
                                                    colorScheme={'green'}
                                                    onClick={() => doRestore([String(item.id)])}
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </Stack>
                    <Stack>
                        <Stack w={'full'} h={'full'} align={'center'} justify={'center'} textAlign={'center'} bg={'gray.500'}>
                            <Heading color={'white'}>Document Viewer</Heading>
                            <Text color={'whiteAlpha.900'}>Select a row to preview details.</Text>
                        </Stack>
                    </Stack>
                    <Stack w={'full'} spacing={6} divider={<StackDivider />}>
                        <Stack direction={'row'} justify={'space-between'} align={'center'}>
                            <Stack direction={'row'}>
                                <IconButton aria-label="Previous" icon={<FaArrowLeft />} rounded={'full'} size={'sm'} colorScheme={'orange'} />
                                <IconButton aria-label="Next" icon={<FaArrowRight />} rounded={'full'} size={'sm'} colorScheme={'orange'} />
                            </Stack>
                        </Stack>
                        <Stack spacing={4}>
                            <Heading fontSize={'lg'}>Document Type</Heading>
                            <Text>{focused?.documentType ?? '—'}</Text>
                        </Stack>
                        <Stack spacing={4}>
                            <Heading fontSize={'lg'}>Document Properties</Heading>
                            <Text>Document Id: <strong>{focused?.id ?? '—'}</strong></Text>
                            <Text>Status: <strong>{focused?.status ?? '—'}</strong></Text>
                            <Text>Uploaded By: <strong>{focused?.user?.email ?? '—'}</strong></Text>
                            <Text>Upload Date: <strong>{focused?.createdAt ?? '—'}</strong></Text>
                            <Text>Source: <strong>{focused?.source ?? '—'}</strong></Text>
                            <Text>Deleted reason: <strong>{focused?.deleteReason ?? '—'}</strong></Text>
                        </Stack>
                    </Stack>
                </SimpleGrid>
            </Stack>

            <Modal isOpen={isDelOpen} onClose={onDelClose} isCentered>
                <ModalOverlay bg={'blackAlpha.400'} backdropFilter={'blur(6px)'} />
                <ModalContent>
                    <ModalHeader>Delete {selected.size} document(s) permanently?</ModalHeader>
                    <ModalBody>
                        <Text fontSize={'sm'} color={'gray.600'} mb={2}>Reason:</Text>
                        <Textarea rows={4} value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder={'Why are you permanently deleting these?'} />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant={'ghost'} mr={2} onClick={onDelClose}>Cancel</Button>
                        <Button colorScheme={'red'} onClick={confirmDelete} isDisabled={!deleteReason.trim()}>Delete</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}
